const GITHUB_API_BASE = 'https://api.github.com';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

export const fetchUserRepos = async (username, perPage = 100) => {
    try {
        // Check cache first
        const cacheKey = `github_repos_${username}`;
        const cached = getFromCache(cacheKey);
        if (cached) {
            console.log('📦 Using cached GitHub repos');
            return cached;
        }

        console.log('🔄 Fetching repos from GitHub API...');
        const response = await fetch(
            `${GITHUB_API_BASE}/users/${username}/repos?sort=updated&per_page=${perPage}`
        );

        if (!response.ok) {
            throw new Error(`GitHub API error: ${response.status}`);
        }

        const repos = await response.json();

        // Cache the results
        saveToCache(cacheKey, repos);

        console.log(`✅ Fetched ${repos.length} repositories`);
        return repos;
    } catch (error) {
        console.error('Error fetching GitHub repos:', error);

        // Return cached data if available, even if expired
        const cacheKey = `github_repos_${username}`;
        const cached = getFromCache(cacheKey, true); // Get even if expired
        if (cached) {
            console.log('⚠️ Using expired cache due to API error');
            return cached;
        }

        throw error;
    }
};

export const fetchRepoDetails = async (username, repoName) => {
    try {
        const response = await fetch(
            `${GITHUB_API_BASE}/repos/${username}/${repoName}`
        );

        if (!response.ok) {
            throw new Error(`GitHub API error: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error fetching repo details for ${repoName}:`, error);
        throw error;
    }
};


export const fetchLatestCommit = async (username, repoName) => {
    try {
        const response = await fetch(
            `${GITHUB_API_BASE}/repos/${username}/${repoName}/commits?per_page=1`
        );

        if (!response.ok) {
            throw new Error(`GitHub API error: ${response.status}`);
        }

        const commits = await response.json();
        return commits[0] || null;
    } catch (error) {
        console.error(`Error fetching latest commit for ${repoName}:`, error);
        return null;
    }
};

export const transformRepoToProject = (repo) => {
    return {
        title: repo.name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        description: repo.description || 'No description available',
        tech: repo.topics && repo.topics.length > 0
            ? repo.topics.slice(0, 5)
            : repo.language
                ? [repo.language]
                : [],
        category: 'github',
        githubUrl: repo.html_url,
        stars: repo.stargazers_count,
        forks: repo.forks_count,
        language: repo.language,
        lastUpdated: repo.updated_at,
        isLive: true,
        homepage: repo.homepage,
        topics: repo.topics || []
    };
};

export const getLanguageColor = (language) => {
    const colors = {
        JavaScript: '#f1e05a',
        TypeScript: '#2b7489',
        Python: '#3572A5',
        Java: '#b07219',
        'C++': '#f34b7d',
        C: '#555555',
        'C#': '#178600',
        PHP: '#4F5D95',
        Ruby: '#701516',
        Go: '#00ADD8',
        Rust: '#dea584',
        Swift: '#ffac45',
        Kotlin: '#F18E33',
        Dart: '#00B4AB',
        HTML: '#e34c26',
        CSS: '#563d7c',
        Shell: '#89e051',
        Vue: '#41b883',
        React: '#61dafb',
    };

    return colors[language] || '#8b949e';
};

export const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};


const saveToCache = (key, data) => {
    try {
        const cacheData = {
            data,
            timestamp: Date.now()
        };
        localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
        console.warn('Failed to save to cache:', error);
    }
};


const getFromCache = (key, ignoreExpiry = false) => {
    try {
        const cached = localStorage.getItem(key);
        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;

        if (ignoreExpiry || age < CACHE_DURATION) {
            return data;
        }

        return null;
    } catch (error) {
        console.warn('Failed to read from cache:', error);
        return null;
    }
};


export const clearGitHubCache = () => {
    try {
        Object.keys(localStorage)
            .filter(key => key.startsWith('github_'))
            .forEach(key => localStorage.removeItem(key));
        console.log('🗑️ GitHub cache cleared');
    } catch (error) {
        console.warn('Failed to clear cache:', error);
    }
};
